import React, { useRef } from 'react';
import stylesInput from '../shared/Input.module.css';
import styles from './InputDate.module.css';

interface Props {
  label: string,
  hint?: string,
  disabled?: boolean,
  state: string,
  day: string,
  month: string,
  year: string,
  includeDay: boolean,
  onChange: ({ day, month, year }: { day?: string, month: string, year: string }) => void,
  className?: string,
  required?: boolean,
  name: string,
  onBlur?: () => void,
}

export default function InputDate({
  label, hint,
  disabled, state,
  day = '',
  month = '',
  year = '',
  includeDay = false,
  onChange,
  className,
  required,
  name,
  onBlur,
}: Props) {

  const dayRef = useRef<HTMLInputElement>(null);
  const monthRef = useRef<HTMLInputElement>(null);
  const yearRef = useRef<HTMLInputElement>(null);

  const getContainerClass = () => {
    if (disabled) {
      return `${styles.disabled} ${className}`;
    }
    if (state === 'SUCCESS') {
      return `${styles.success} ${className}`;
    }
    if (state === 'ERROR') {
      return `${styles.container} ${className}`;
    }
    return `${styles.container} ${className}`;
  };

  const getInputClass = () => {
    if (disabled) {
      return `${stylesInput.input} ${styles.inputDisabled}`;
    }
    if (state === 'SUCCESS') {
      return `${stylesInput.input} ${styles.inputSuccess}`;
    }
    if (state === 'ERROR') {
      return `${stylesInput.input} ${styles.inputError}`;
    }
    return stylesInput.input;
  };

  const getHintClass = () => {
    if (disabled) {
      return `${styles.hint} ${styles.hintDisabled}`;
    }
    if (state === 'SUCCESS') {
      return `${styles.hint} ${styles.hintSuccess}`;
    }
    if (state === 'ERROR') {
      return `${styles.hint} ${styles.hintError}`;
    }
    return styles.hint;
  };

  const handleMonthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value === '') {
      if (includeDay) {
        onChange({ day: day, month: '', year: year });
      } else {
        onChange({ month: '', year: year });
      }
      return;
    }
    const monthInput = parseInt(e.target.value);
    if (!isNaN(monthInput)) {
      if (monthInput >= 0 && monthInput <= 12) {
        if (includeDay) {
          onChange({ day: day, month: e.target.value, year: year });
        } else {
          onChange({ month: e.target.value, year: year });
        }
        if (e.target.value.length === 2 && monthRef.current && yearRef.current) {
          monthRef.current.blur();
          yearRef.current.focus();
        }
      }
    }
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value === '') {
      if (includeDay) {
        onChange({ day: day, month: month, year: '' });
      } else {
        onChange({ month: month, year: '' });
      }
      return;
    }
    const yearInput = e.target.value;
    if (!isNaN(parseInt(yearInput)) && yearInput.length < 5) {
      if (includeDay) {
        onChange({ day: day, month: month, year: e.target.value });
      } else {
        onChange({ day: day, month: month, year: e.target.value });
      }
    }
  };

  const handleDayChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value === '') {
      onChange({ day: '', month: month, year: year });
      return;
    }
    const dayInput = parseInt(e.target.value);
    if (!isNaN(dayInput)) {
      if (dayInput >= 0 && dayInput <= 31) {
        onChange({ day: dayInput.toString(), month: month, year: year });
        if (e.target.value.length === 2 && dayRef.current && monthRef.current) {
          dayRef.current.blur();
          monthRef.current.focus();
        }
      }
    }
  };

  return (
    <div className={getContainerClass()}>
      <p
        className={styles.label}
      >
        {(required) && (<span className={styles.required}> * </span>)}
        {label}
      </p>
      <div className={styles.row}>
        <div
          className={getInputClass()}
          hidden={!includeDay}
        >
          <input
            ref={dayRef}
            type="text"
            className={styles.month}
            placeholder="DD"
            value={day || ''}
            onChange={handleDayChange}
            name={(name) ? (name) : undefined}
            onBlur={onBlur}
            data-cy={(`${name || 'date'}-day`)}
          />
        </div>
        <span
          className={styles.separator}
          hidden={!includeDay}
        >
          /
        </span>
        <div className={getInputClass()}>
          <input
            ref={monthRef}
            type="text"
            className={styles.month}
            placeholder="MM"
            value={month || ''}
            onChange={handleMonthChange}
            name={(name) ? (name) : undefined}
            onBlur={onBlur}
            data-cy={(`${name || 'date'}-month`)}
          />
        </div>
        <span className={styles.separator}>
          /
        </span>
        <div className={getInputClass()}>
          <input
            ref={yearRef}
            type="text"
            className={styles.year}
            placeholder="YYYY"
            value={year || ''}
            onChange={handleYearChange}
            name={(name) ? (name) : undefined}
            onBlur={onBlur}
            data-cy={(`${name || 'date'}-year`)}
          />
        </div>
      </div>
      {
        (hint)
        && (
          <p
            className={getHintClass()}
          >
            {hint}
          </p>
        )
      }
    </div>
  );
}
